/**
 * TutorCitationDialog — TASK-012 REVISIONE 2 Parte C: the in-page popup shown
 * when a student clicks a citation in the tutor marginalia
 * (TutorMessageBubble.tsx). The marginalia quote is only a CSS-clamped
 * excerpt (line-clamp-3) — this shows the cited block's FULL text (fetched
 * on demand, Tutor::BlockContentsController) so the student can verify the
 * source without leaving the conversation, plus an "Apri nel manuale" link
 * that still does the original direct deep-link.
 *
 * Purely presentational, same posture as TutorMessageBubble.tsx: it owns NO
 * fetching/effect of its own. TutorOverlay.tsx's handleOpenCitation (a plain
 * click-event handler, not a useEffect) kicks off the fetch the moment the
 * citation is clicked and passes the resulting status/block/errorMessage in
 * as props — this component just renders whatever it's given.
 *
 * Styling matches the fixed-inset backdrop + centered panel pattern already
 * used for the admin delete-confirmation dialog
 * (app/javascript/pages/admin/Manuals.tsx), sized/tinted for the tutor's own
 * surface tokens (bg/surface/line/accent) instead of DS Button/Heading —
 * TutorOverlay/TutorMessageBubble never use those either, they hand-roll
 * every element with the same Tailwind tokens, and this dialog matches that
 * established local convention.
 */

import * as React from 'react'
import { MarkdownBody } from '@arianna/reader-ui'
import Text from '@arianna/design-system/Text'
import type { TutorBlockContent } from '../tutorApi.js'
import type { TutorCitationView } from './TutorMessageBubble.js'

export type TutorCitationDialogStatus = 'loading' | 'ready' | 'error'

export interface TutorCitationDialogProps {
  citation: TutorCitationView
  status: TutorCitationDialogStatus
  block: TutorBlockContent | null
  errorMessage: string | null
  onClose: () => void
  onOpenInManual: () => void
}

export function TutorCitationDialog({
  citation,
  status,
  block,
  errorMessage,
  onClose,
  onOpenInManual,
}: TutorCitationDialogProps) {
  // Prefers the freshly-fetched block's title (server-resolved from the
  // CURRENT toc, same resolution Tutor::CitationBuilder itself uses) once
  // available; falls back to the citation's own (already-persisted/live)
  // title so the header isn't blank while the fetch is in flight.
  const resolvedTitle = block?.title || citation.title || null
  const heading =
    citation.sub_para_id && resolvedTitle
      ? `${citation.sub_para_id} — ${resolvedTitle}`
      : `Sezione ${citation.section_id}`

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutor-citation-dialog-title"
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/45 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="bg-bg border border-line rounded-lg max-w-[560px] w-full max-h-[75vh] flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-line shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] text-ink-3 font-sans font-semibold uppercase tracking-[0.12em]">
              Citazione dal manuale
            </div>
            <h2
              id="tutor-citation-dialog-title"
              className="font-serif text-[19px] font-semibold tracking-[-0.01em] leading-tight text-ink truncate mt-0.5"
            >
              {heading}
            </h2>
          </div>
          <button
            onClick={onClose}
            title="Chiudi"
            aria-label="Chiudi"
            className="inline-flex items-center justify-center size-7 rounded text-ink-4 hover:text-ink bg-transparent border-none cursor-pointer shrink-0"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {status === 'loading' && <Text variant="meta">Caricamento…</Text>}
          {status === 'error' && (
            <Text variant="meta" className="text-red">
              {errorMessage ?? 'Impossibile recuperare il testo del blocco.'}
            </Text>
          )}
          {status === 'ready' && block != null && <MarkdownBody markdown={block.text} />}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line shrink-0">
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm text-ink-2 bg-transparent border border-line cursor-pointer hover:bg-surface-2 transition-colors"
          >
            Chiudi
          </button>
          <button
            onClick={onOpenInManual}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-accent bg-accent-bg border border-accent cursor-pointer hover:opacity-90 transition-opacity"
          >
            Apri nel manuale <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  )
}
